import React, { useState, useRef, useEffect } from 'react';

import { Icon } from '@greenlightme/ui/dist/v2';
import classNames from 'classnames';
import { Checkbox } from '../AtomicComponents/Checkbox';

interface Props {
  filter: 'department' | 'location';
  defaultSelection: string;
  dropdownText: string | null;
  currentSelections: string[] | null;
  handleSelection: (
    val: string | null,
    filter: 'department' | 'location'
  ) => void;
  dropdownOptions: string[];
}

export function Dropdown({
  filter,
  dropdownText,
  dropdownOptions,
  defaultSelection,
  currentSelections,
  handleSelection
}: Props) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isMenuOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const onDropdownSelect = (
    e: React.MouseEvent | React.KeyboardEvent,
    selection: string | null
  ) => {
    e.stopPropagation();
    handleSelection(selection, filter);
  };

  return (
    <div
      ref={dropdownRef}
      className="flex h-14 flex-col items-center self-stretch overflow-visible bg-white tablet:w-1/2 tablet:max-w-[566px] tablet:self-auto tablet:[&_*]:max-w-[566px]"
      data-testid={`${filter}-dropdown`}
      aria-expanded={isMenuOpen}
      aria-haspopup="listbox"
    >
      <div
        className={classNames(
          'flex flex-col items-start justify-center self-stretch border bg-white px-4',
          filter === 'department' ? 'z-[3]' : 'z-[2]',
          isMenuOpen
            ? 'rounded-t-button-shape border-tertiary'
            : 'rounded-button-shape border-line',
          'relative'
        )}
      >
        {/* Dropdown header: only this toggles the menu */}
        <div
          className="flex h-14 flex-grow cursor-pointer items-center gap-3 self-stretch"
          onClick={() => setIsMenuOpen((open) => !open)}
          onKeyDown={(e) => {
            if (['Enter', 'Space', ' '].includes(e.key)) {
              setIsMenuOpen((open) => !open);
            }
          }}
          role="button"
          tabIndex={0}
        >
          <p className="typography-title-4 flex h-6 flex-grow select-none items-center">
            {dropdownText}
          </p>
          <Icon
            alt="Down arrow to open dropdown list"
            aria-label="Down arrow to open dropdown list"
            fileName={'ic_chevron_down'}
            size={'xl'}
            className={classNames(
              '-mr-1 flex aspect-square items-center justify-center p-1 transition-transform duration-300',
              {
                'rotate-180': isMenuOpen
              }
            )}
          />
        </div>
        {/* Dropdown content: clicking inside here does not close the menu */}
        <div
          className={classNames(
            'absolute left-[-1px] top-full z-10 flex h-auto w-[calc(100%+2px)] origin-top flex-col items-start gap-4 overflow-hidden border-x border-b bg-white px-4 transition-transform duration-300 tablet:max-w-[566px]',
            isMenuOpen
              ? 'pointer-events-auto scale-y-100 border-tertiary pb-3 opacity-100'
              : 'pointer-events-none scale-y-0 border-line opacity-0',
            'rounded-b-button-shape'
          )}
          role="listbox"
          tabIndex={-1}
          aria-activedescendant={undefined}
        >
          <div
            className="flex w-full select-none items-center gap-4 self-stretch"
            role="option"
            aria-selected={currentSelections?.length === 0}
            tabIndex={0}
            onClick={(e) => onDropdownSelect(e, null)}
            onKeyDown={(e) => {
              if (['Enter', 'Space', ' '].includes(e.key)) {
                onDropdownSelect(e, null);
              }
            }}
          >
            <button
              type="button"
              className="typography-title-4 bg-transparent flex flex-grow cursor-pointer select-none flex-col items-start justify-center gap-[2px] self-stretch border-none p-0"
              data-testid={`all-${filter}-anchor`}
            >
              {defaultSelection}
            </button>
            <Checkbox
              name="select-all"
              id="select-all"
              checked={currentSelections === null}
              readOnly
              size="sm"
            />
          </div>
          {dropdownOptions.sort().map((selection: string) => (
            <div
              className="flex w-full select-none items-center gap-4 self-stretch"
              key={selection}
            >
              <button
                type="button"
                className="flex w-full flex-grow cursor-pointer select-none items-center self-stretch border-none p-0"
                onClick={(e) => onDropdownSelect(e, selection)}
                data-testid={selection}
              >
                <div className="typography-title-4 bg-transparent flex flex-grow cursor-pointer select-none flex-col items-start justify-center gap-[2px] self-stretch border-none p-0 text-start">
                  {selection}
                </div>
                <Checkbox
                  name={`select-${selection
                    .replace(/\s+/g, '-')
                    .toLowerCase()}`}
                  data-testid={`select-${selection
                    .replace(/\s+/g, '-')
                    .toLowerCase()}`}
                  id={`select-${selection.replace(/\s+/g, '-').toLowerCase()}`}
                  checked={
                    currentSelections === null ||
                    (currentSelections &&
                      currentSelections.includes(selection)) ||
                    false
                  }
                  readOnly
                  size="sm"
                />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
